import { getObjectKeys } from './getObjectKeys';

/**
 * A class that makes generating url's easier
 *
 * @example
 *   interface IExample {
 *       prop: number;
 *       prop2: string;
 *   }
 *
 *   const urlBuilder = new UrlBuilder<IExample>('/api/example');
 *   const url = urlBuilder
 *   .add('prop', 5)
 *   .add('prop2', 'examplePropValue')
 *   .build(); // '/api/example?prop=5&prop2=examplePropValue'
 */
export class UrlBuilder<QueryParameters extends object = any> {
    private urlObject: { [k in keyof QueryParameters | string]?: string } = {};

    /**
     * Create a new UrlBuilder instance, to start creating your url.
     * You can pass an interface as generic type to the UrlBuilder and the UrlBuilder will be typed.
     * @param base The base value of the url, something like '/api'.
     * @param options Additional options to further tweak the builder.
     */
    constructor(
        public readonly base?: string,
        private readonly options: IUrlBuilderOptions = { isFirstParameter: true }
    ) {}

    /** The keyValueObject overwrites already existing keys, but does not clear existing keys not in the keyValueObject */

    public add<K extends keyof QueryParameters>(keyValueObject: Partial<QueryParameters>): UrlBuilder<QueryParameters>;
    public add<K extends keyof QueryParameters>(key: K, value: ValueType): UrlBuilder<QueryParameters>;
    public add<K extends keyof QueryParameters>(keyOrKeyValueObject: K | Partial<QueryParameters>, value?: ValueType) {
        if (typeof keyOrKeyValueObject === 'string') {
            if (Array.isArray(value)) {
                value = value.join(',');
            }
            this.urlObject[keyOrKeyValueObject] = encodeURIComponent(String(value));
        } else {
            for (const key of getObjectKeys(keyOrKeyValueObject)) {
                const loopValue = (keyOrKeyValueObject as any)[key];
                this.add(key, loopValue);
            }
        }

        return this;
    }

    public clear() {
        this.urlObject = {};

        return this;
    }

    public build() {
        let baseUrl = this.base ? this.base : '';
        if (Object.keys(this.urlObject).length === 0) {
            return baseUrl;
        }

        if (this.options.isFirstParameter) {
            baseUrl += '?';
        } else {
            baseUrl += '&';
        }

        return (
            baseUrl +
            Object.keys(this.urlObject)
                .map((key) => {
                    const value = this.urlObject[key];

                    return `${key}=${value}`;
                })
                .join('&')
        );
    }
}

interface IUrlBuilderOptions {
    /** If the builder should include a '?' after the base string. Defaults to true */
    isFirstParameter: boolean;
}

type ValueType = number | string | boolean | number[] | string[] | boolean[];
